// Report-param grammar for the shelf-test block, ported from the coffee block
// (importReportParams / importSortReportParams / updateReportParams / updateChartReportParams /
// onZoomChange).
//
// The block owns seven params, addressed by prefix: tcv (chart view), ccm (chart collapsed),
// tscv (shelf view), csm (shelf collapsed), cm (chart mode), z (zoom) and s (sort order). The
// page view expands each prefix with the question key on the way in and on the way out.

import { getDefaultChartMode } from '@aytm/app/stats/page/question-views/sh/components/chart/lib/chartModes'
import {
  DEFAULT_SORT_ORDER,
  decodeSortParam,
  encodeSortParam
} from '@aytm/app/stats/page/question-views/components/lib/sort-param'
import { decodeZoomParam, encodeZoomParam } from '@aytm/app/stats/page/question-views/components/lib/zoom-param'

export const DEFAULT_CHART_VIEW = 'amount'
export const DEFAULT_SHELF_VIEW = 'heatmap'
export { DEFAULT_SORT_ORDER }

// Unlike the items-list base this falls back to the default on an unrecognised token instead of
// storing undefined.
export const resolveSortOrder = (section, defaultSortOrder = DEFAULT_SORT_ORDER) =>
  decodeSortParam(section, { defaultSortOrder })

const resolveViewMode = (section, fallback) => (section && String(section).split('|')[0]) || fallback

export const resolveReportParams = ({ params = {}, defaultSortOrder } = {}) => ({
  viewMode: resolveViewMode(params.tcv, DEFAULT_CHART_VIEW),
  chartCollapsed: params.ccm ?? false,
  shelfViewMode: resolveViewMode(params.tscv, DEFAULT_SHELF_VIEW),
  shelfCollapsed: params.csm ?? false,
  chartMode: params.cm || null,
  zoomLevel: decodeZoomParam(params.z),
  sortOrder: resolveSortOrder(params.s, defaultSortOrder)
})

// The mode the chart opens on once the question and report-wide defaults are applied -- what the
// exporters draw by, as opposed to the stored `cm`, which is null until the researcher picks one.
export const calcResolvedChartMode = (state = {}, context = {}) =>
  getDefaultChartMode({
    chartMode: state.chartMode,
    globalChartMode: context.globalChartMode,
    modelChartMode: context.modelChartMode,
    answerVariants: context.answerVariants,
    viewMode: state.viewMode
  })

// The whole param set the block owns, keyed by prefix. Every entry is written on every change:
// a value that did not move resolves to what is already stored, and a null clears the param.
export const calcReportParamsSection = (state = {}, context = {}) => {
  const { viewMode, chartCollapsed, shelfViewMode, shelfCollapsed, chartMode, zoomLevel, sortOrder } = state
  const { format = {}, modelChartMode, globalZoomLevel, defaultSortOrder } = context

  // the mode the question itself declares; a selection matching it needs no param
  const declaredChartMode = format.chartMode || modelChartMode || 'off'
  const resolvedChartMode = calcResolvedChartMode(state, context)

  return {
    tcv: DEFAULT_CHART_VIEW === viewMode ? null : viewMode,
    ccm: chartCollapsed || null,
    tscv: DEFAULT_SHELF_VIEW === shelfViewMode ? null : shelfViewMode,
    csm: shelfCollapsed || null,
    cm: chartMode === declaredChartMode ? null : chartMode,
    // a pie has no axis to zoom, and a zoom matching the report-wide one needs no param
    z: 'pie' === resolvedChartMode ? null : encodeZoomParam(zoomLevel, globalZoomLevel),
    s: encodeSortParam(sortOrder, { defaultSortOrder })
  }
}
